import request from '@/utils/request'

// ==================== 通用学习计划接口 ====================

/**
 * 创建学习计划
 * @param {Object} data 学习计划数据
 */
export function createPlan(data) {
  return request({
    url: '/study-plans',
    method: 'post',
    data
  })
}

/**
 * 更新学习计划
 * @param {Object} data 学习计划数据
 */
export function updatePlan(data) {
  return request({
    url: '/study-plans',
    method: 'put',
    data
  })
}

/**
 * 根据用户ID获取所有学习计划
 * @param {Number} userId 用户ID
 */
export function getPlansByUserId(userId) {
  return request({
    url: `/study-plans/user/${userId}`,
    method: 'get'
  })
}

/**
 * 根据用户ID和题库ID获取学习计划
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID
 */
export function getPlanByUserIdAndRepoId(userId, repoId, sessionCode) {
  return request({
    url: `/study-plans/user/${userId}/repo/${repoId}`,
    method: 'get',
    params: sessionCode ? { sessionCode } : {}
  })
}

/**
 * 根据用户ID和课程类型获取学习计划
 * @param {Number} userId 用户ID
 * @param {String} courseType 课程类型
 */
export function getPlansByUserIdAndCourseType(userId, courseType) {
  return request({
    url: `/study-plans/user/${userId}/type/${courseType}`,
    method: 'get'
  })
}

/**
 * 获取用户当前激活的学习计划
 * @param {Number} userId 用户ID
 * @param {String} courseType 课程类型（可选）
 */
export function getActivePlan(userId, courseType) {
  return request({
    url: `/study-plans/user/${userId}/active`,
    method: 'get',
    params: courseType ? { courseType } : {}
  })
}

/**
 * 设置学习计划为激活状态
 * @param {Number} userId 用户ID
 * @param {Number} planId 计划ID
 */
export function setActivePlan(userId, planId) {
  return request({
    url: `/study-plans/user/${userId}/active/${planId}`,
    method: 'put'
  })
}

/**
 * 分页查询学习计划
 * @param {Object} params 查询参数 {pageNum, pageSize, userId, courseType, repoId}
 */
export function getPlanPaging(params) {
  return request({
    url: '/study-plans/paging',
    method: 'get',
    params
  })
}

/**
 * 删除学习计划
 * @param {Number} planId 计划ID
 */
export function deletePlan(planId) {
  return request({
    url: `/study-plans/${planId}`,
    method: 'delete'
  })
}

// ==================== 通用学习方案接口 ====================

/**
 * 创建学习方案
 * @param {Object} data 学习方案数据
 */
export function createScheme(data) {
  return request({
    url: '/study-schemes',
    method: 'post',
    data
  })
}

/**
 * 更新学习方案
 * @param {Object} data 学习方案数据
 */
export function updateScheme(data) {
  return request({
    url: '/study-schemes',
    method: 'put',
    data
  })
}

/**
 * 根据用户ID获取所有学习方案
 * @param {Number} userId 用户ID
 */
export function getSchemesByUserId(userId) {
  return request({
    url: `/study-schemes/user/${userId}`,
    method: 'get'
  })
}

/**
 * 根据用户ID和题库ID获取学习方案
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID
 */
export function getSchemeByUserIdAndRepoId(userId, repoId) {
  return request({
    url: `/study-schemes/user/${userId}/repo/${repoId}`,
    method: 'get'
  })
}

/**
 * 根据学习计划ID获取学习方案
 * @param {Number} planId 计划ID
 */
export function getSchemeByPlanId(planId) {
  return request({
    url: `/study-schemes/plan/${planId}`,
    method: 'get'
  })
}

/**
 * 更新已完成题目列表
 * @param {Number} schemeId 方案ID
 * @param {String} finishedIds 已完成题目ID列表
 */
export function updateSchemeFinishedIds(schemeId, finishedIds) {
  return request({
    url: `/study-schemes/${schemeId}/finished`,
    method: 'put',
    params: { finishedIds }
  })
}

// ==================== 通用每日学习进度接口 ====================

/**
 * 创建或更新今日计划
 * @param {Object} params {userId, schemeId, repoId, date, questionIds}
 */
export function upsertTodayPlan(params) {
  return request({
    url: '/study-progress/upsert-today',
    method: 'post',
    params
  })
}

/**
 * 获取指定日期的学习进度
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID
 * @param {String} date 日期（可选，格式：yyyy-MM-dd）
 */
export function getProgressByDate(userId, repoId, date) {
  return request({
    url: `/study-progress/user/${userId}/repo/${repoId}/date`,
    method: 'get',
    params: date ? { date } : {}
  })
}

/**
 * 分页查询学习进度
 * @param {Object} params {pageNum, pageSize, userId, repoId, dateFrom, dateTo}
 */
export function getProgressPaging(params) {
  return request({
    url: '/study-progress/paging',
    method: 'get',
    params
  })
}

/**
 * 更新已完成题目
 * @param {Number} progressId 进度ID
 * @param {String} finishedId 已完成题目ID列表
 * @param {Number} correctCount 正确数量
 * @param {Number} wrongCount 错误数量
 * @param {Number} reviewDone 复习完成数量
 */
export function updateProgressFinished(progressId, finishedId, correctCount, wrongCount, reviewDone) {
  return request({
    url: `/study-progress/${progressId}/finished`,
    method: 'put',
    params: { finishedId, correctCount, wrongCount, reviewDone }
  })
}

/**
 * 统计已完成天数
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID（可选）
 * @param {String} dateFrom 开始日期（可选）
 * @param {String} dateTo 结束日期（可选）
 */
export function countCompletedDays(userId, repoId, dateFrom, dateTo) {
  return request({
    url: `/study-progress/user/${userId}/completed-days-with-dates`,
    method: 'get',
    params: { repoId, dateFrom, dateTo }
  })
}

/**
 * 获取本月已完成日期列表
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID（可选）
 */
export function getCompletedDatesThisMonth(userId, repoId) {
  return request({
    url: `/study-progress/user/${userId}/completed-dates-this-month`,
    method: 'get',
    params: repoId ? { repoId } : {}
  })
}

/**
 * 计算学习深度
 * @param {Number} userId 用户ID
 * @param {Object} data 学习计划数据
 */
export function calcStudyDepth(userId, data) {
  return request({
    url: `/study-plans/user/${userId}/study-depth`,
    method: 'post',
    data
  })
}

/**
 * 按深度比例生成题目清单并写入学习方案
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID
 * @param {Number} depthSnapshot 深度比例 (0-1)
 */
export function generateScheme(userId, repoId, depthSnapshot) {
  return request({
    url: '/study-schemes/generate',
    method: 'post',
    params: { userId, repoId, depthSnapshot }
  })
}

/**
 * 重置用户学习计划（删除计划、方案、进度、做题记录）
 * @param {Number} userId 用户ID
 * @param {Number} repoId 题库ID（可选，不传则删除所有题库的数据）
 * @param {Boolean} clearRecords 是否清除做题记录（可选，默认true）
 */
export function resetStudyPlan(userId, repoId, clearRecords = true) {
  const params = {}
  if (repoId) params.repoId = repoId
  if (clearRecords !== undefined) params.clearRecords = clearRecords
  return request({
    url: `/study-plans/reset/${userId}`,
    method: 'post',
    params
  })
}
